
import React, { useState, useRef, useEffect } from "react";
import { cn } from "../lib/utils";
import { Textarea } from "./ui/textarea";
import { Eye, EyeOff } from "lucide-react";

interface ChatInputProps {
  onSendMessage: (message: string) => void;
  isTyping: boolean;
  messagesHidden?: boolean;
  onToggleMessages?: () => void;
}

const ChatInput: React.FC<ChatInputProps> = ({ onSendMessage, isTyping, messagesHidden = false, onToggleMessages }) => {
  const [inputValue, setInputValue] = useState("");
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const handleSendMessage = () => {
    if (inputValue.trim() === "") return;
    onSendMessage(inputValue);
    setInputValue("");

    if (inputRef.current) {
      inputRef.current.style.height = "auto";
      // Re-focus after sending
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const autoResize = () => {
    const textarea = inputRef.current;
    if (textarea) {
      textarea.style.height = "auto";
      textarea.style.height = `${Math.min(textarea.scrollHeight, 120)}px`;
    }
  };

  useEffect(() => {
    autoResize();
  }, [inputValue]);

  // Focus on mount and whenever typing stops (bot finished responding)
  useEffect(() => {
    if (!isTyping) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isTyping]);

  return (
    <div className="px-3 py-3 sm:px-4 sm:py-4">
      <div
        className="relative flex flex-col rounded-2xl"
        style={{
          WebkitBackdropFilter: 'blur(24px)',
          backdropFilter: 'blur(24px)',
          background: 'rgba(0, 0, 0, 0.35)',
          border: '1px solid rgba(255, 255, 255, 0.15)',
        }}
      >
        <Textarea
          ref={inputRef}
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Type your response..."
          className="resize-none px-4 pt-3 pb-1 text-base sm:text-sm focus-visible:ring-0 focus-visible:ring-offset-0 min-h-[44px] max-h-[120px] placeholder:text-muted-foreground"
          style={{ backgroundColor: 'transparent', border: 'none' }}
          disabled={isTyping}
          onInput={autoResize}
          rows={1}
        />
        <div className="flex items-center justify-between px-3 pb-2 pt-1">
          {onToggleMessages ? (
            <button
              onClick={onToggleMessages}
              className="rounded-xl p-2 h-8 w-8 flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors"
              aria-label={messagesHidden ? "Show messages" : "Hide messages"}
              title={messagesHidden ? "Show messages" : "Hide messages"}
            >
              {messagesHidden ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          ) : <div />}
          <button
            onClick={handleSendMessage}
            disabled={isTyping || inputValue.trim() === ""}
            className={cn(
              "rounded-xl p-2 transition-all h-8 w-8 flex items-center justify-center shrink-0",
              isTyping || inputValue.trim() === ""
                ? "bg-muted text-muted-foreground cursor-not-allowed"
                : "bg-primary text-primary-foreground hover:bg-primary/90 shadow-sm"
            )}
            aria-label="Send message"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M12 19V5" />
              <path d="m5 12 7-7 7 7" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
};

export default ChatInput;
